import { useContext, useEffect, useRef } from 'react';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { ChatSessionPendingAttachmentsContext } from '../context';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 80; // ~2 minutes, past the 60s parser timeout plus one retry

// Polls the extraction-status endpoint while any attachment is still pending,
// so the chip and the send button can reflect it.
export function useAttachmentExtractionStatus(): void {
  const pendingAttachments = useContext(ChatSessionPendingAttachmentsContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const apiClient = useChatApiClient();
  const attemptsRef = useRef(0);

  const pendingIdentifiers = pendingAttachments
    .filter((a) => a.identifier.length > 0 && a.extraction?.status === 'pending')
    .map((a) => a.identifier);
  const key = pendingIdentifiers.join(',');

  useEffect(() => {
    if (key.length === 0) {
      attemptsRef.current = 0;
      return;
    }

    let cancelled = false;

    const poll = async (): Promise<void> => {
      attemptsRef.current += 1;
      try {
        const { statuses } = await apiClient.chat.getInputAttachmentExtractionStatus({
          identifiers: key.split(','),
        });
        if (cancelled) return;

        for (const status of statuses) {
          // An identifier the server does not know cannot become ready; stop waiting on it.
          const resolved = status.status === 'not_found' ? 'failed' : status.status;
          dispatch({
            type: 'pending-attachment/extraction-updated',
            payload: {
              identifier: status.identifier,
              status: resolved,
              error: status.error ?? undefined,
            },
          });
        }
      } catch {
        if (cancelled) return;
        // Leave the attachments pending and let the next tick retry; a hard fail
        // here would strand the send button.
      }
    };

    const timer = setInterval(() => {
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(timer);
        for (const identifier of key.split(',')) {
          dispatch({
            type: 'pending-attachment/extraction-updated',
            payload: { identifier, status: 'failed', error: 'parse_failed' },
          });
        }
        return;
      }
      poll().catch(() => undefined);
    }, POLL_INTERVAL_MS);

    poll().catch(() => undefined);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [apiClient.chat, dispatch, key]);
}
